import React, { useEffect, useRef, useState } from 'react';
import { styled as styled2 } from '@compiled/react';
import Button from '@atlaskit/button';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type {
	Language,
	TranslationField,
} from '@atlassian/jira-servicedesk-language-settings-translations-resource/src/services/get-data/types.tsx';
import {
	useFieldState,
	useFormDirty,
	useManageTranslationsActions,
} from '../../../controllers/manage-translations/index.tsx';
import messages from './messages';

type FormElementProps = HTMLTextAreaElement | HTMLInputElement;

// This is an uncontrolled text field because of an existing react bug that fires the onChange event before the onCompositionStart has fired in controlled components: https://github.com/facebook/react/issues/8683.
// This means that if the user types a key, the onChange event will fire and trigger onCompositionStart per key press if this was controlled - Which would cause IME inputs to not work properly as they rely on the onCompositionStart to only occur once.
// So instead, we will update the value of the store on change but not the value of the text field itself.
export const FlaggableTextField = ({
	translationField,
	selectedLanguage,
	isReadOnly,
}: {
	translationField: TranslationField;
	selectedLanguage: Language;
	isReadOnly: boolean;
}) => {
	const [isFocused, setIsFocus] = useState(false);
	const isDirty = useFormDirty();
	const ref = useRef<HTMLTextAreaElement>(null);
	const { formatMessage } = useIntl();
	const { updateFields, confirmTranslation } = useManageTranslationsActions();
	const value = translationField.translationContentMap[selectedLanguage.key];
	const fieldState = useFieldState(translationField.logicalId);
	const originalValue = useRef(value);

	useEffect(() => {
		if (ref.current) {
			ref.current.value = value;
		}
	}, [value]);

	useEffect(() => {
		if (ref.current && fieldState && !isDirty && fieldState?.changeType === 'changed') {
			ref.current.value = originalValue.current;
		}
	}, [isDirty, fieldState]);

	const handleFocus: React.FocusEventHandler<FormElementProps> = () => {
		setIsFocus(true);
	};

	const handleChange: React.FormEventHandler<FormElementProps> = (
		e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const payload = {
			fieldContent: e.currentTarget.value,
			fieldLogicalId: translationField.logicalId,
		};
		updateFields(payload);
	};

	const handleBlur: React.FocusEventHandler<FormElementProps> = () => {
		setIsFocus(false);
	};

	const handleConfirmTranslation = () => {
		const payload = {
			fieldLogicalId: translationField.logicalId,
		};

		confirmTranslation(payload);
	};

	const props = {
		ref,
		onChange: handleChange,
		onBlur: handleBlur,
		onFocus: handleFocus,
		name: translationField.displayName,
		'aria-label': translationField.displayName,
		isReadOnly,
		isDisabled: isReadOnly,
		inputMode: 'text' as const,
		placeholder: formatMessage(messages.fieldPlaceholder),
	};

	const shouldShowTextArea =
		translationField.inputType === 'textarea' ||
		translationField.logicalId.indexOf('helptext') > -1;

	return (
		<TextFieldWrapper showFlag={!isFocused && translationField.translationState !== 'up_to_date'}>
			{shouldShowTextArea ? <TextArea {...props} /> : <Textfield {...props} />}
			{translationField.translationState === 'outdated' && (
				<Button appearance="link" onClick={handleConfirmTranslation}>
					{formatMessage(messages.title)}
				</Button>
			)}
		</TextFieldWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const TextFieldWrapper = styled2.div<{ showFlag: boolean }>(({ showFlag }) =>
	showFlag
		? `& [data-ds--text-field--container]:not(:focus-within) {
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
border-left: 5px solid ${token('color.border.warning', colors.Y200)};
transition: border-left 0.05s ease-in;
},& textarea:not(:focus-within) {
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
    border-left: 5px solid ${token('color.border.warning', colors.Y200)};
    transition: border-left 0.05s ease-in;
}`
		: undefined,
);
